/**
 * OrgTrackingSettings - Admin UI for configuring tracking number prefix.
 */

import React, { useState, useEffect } from 'react';
import { Settings2, Save, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import hardCopyTrackingAPI from '@/services/api/hardCopyTracking';

export function OrgTrackingSettings() {
  const [prefix, setPrefix] = useState('DOC');
  const [nextNumber, setNextNumber] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await hardCopyTrackingAPI.getOrgSettings();
      setPrefix(data.prefix);
      setNextNumber(data.next_number);
    } catch {
      // Defaults are fine
    }
  };

  const handleSave = async () => {
    const cleaned = prefix.toUpperCase().replace(/[^A-Z0-9-_]/g, '');
    if (cleaned.length < 2 || cleaned.length > 10) {
      setError('Prefix must be 2-10 characters (letters, numbers, hyphens)');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const data = await hardCopyTrackingAPI.updateOrgSettings(cleaned);
      setPrefix(data.prefix);
      setNextNumber(data.next_number);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const previewNumber = `${prefix}-${String(nextNumber).padStart(5, '0')}`;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="w-4 h-4" />
          Tracking Number Configuration
        </CardTitle>
        <CardDescription>
          Configure the prefix used for hard-copy document tracking numbers.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Prefix</Label>
            <Input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase())}
              placeholder="DOC"
              maxLength={10}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground mt-1">
              2-10 characters: letters, numbers, hyphens
            </p>
          </div>
          <div>
            <Label>Next Number</Label>
            <div className="flex items-center gap-2 h-10">
              <Hash className="w-4 h-4 text-muted-foreground" />
              <span className="font-mono text-sm">{nextNumber}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-md">
          <span className="text-sm text-muted-foreground">Preview:</span>
          <Badge variant="primary" size="md" className="font-mono">
            {previewNumber}
          </Badge>
        </div>

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <Button onClick={handleSave} disabled={saving}>
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : success ? 'Saved!' : 'Save Prefix'}
        </Button>
      </CardContent>
    </Card>
  );
}
